import { notifications } from "@mantine/notifications";
import { isAxiosError } from "axios";
import {
  createUserWithEmailAndPassword,
  getAuth,
  GoogleAuthProvider,
  signInWithCustomToken,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  UserCredential,
} from "firebase/auth";
import { AuthConnector } from "../serverConnectors";
import { setUser } from "../store/authSlice";
import store from "../store/store";
import { AuthServiceResponse, Roles, User } from "../types";
import { AuthLocationHintService } from "./auth-location-hint-service";
import { LinkedInAuthClient } from "./linkedin-auth-client";

const authConnector = new AuthConnector();
const authLocationHintService = new AuthLocationHintService();
const linkedInAuth = new LinkedInAuthClient(authConnector, authLocationHintService);
type AuthAction = "login" | "register";

function authErrorMessage(error: unknown): string {
  if (isAxiosError<{ message?: string }>(error)) {
    return error.response?.data?.message ?? error.message;
  }
  if (error && typeof error === "object" && "code" in error) {
    switch (error.code) {
      case "auth/account-exists-with-different-credential":
        return "An account already uses this email. Log in with your existing sign-in method.";
      case "auth/popup-blocked":
        return "Allow pop-ups for jobsRmine, then try again.";
      case "auth/operation-not-allowed":
        return "This sign-in method is not available yet. Please use another method.";
    }
  }
  return error instanceof Error
    ? error.message
    : "Authentication failed. Please try again.";
}

async function authenticate(
  credential: () => Promise<UserCredential>,
  action: AuthAction,
  label: string,
  role?: Roles,
): Promise<User | null> {
  try {
    const { user: firebaseUser } = await credential();
    if (!firebaseUser.email) {
      throw new Error(
        "Your sign-in provider did not share an email address. Please use another sign-in method.",
      );
    }
    const idToken = await firebaseUser.getIdToken();
    const identity = { uid: firebaseUser.uid, email: firebaseUser.email, idToken };
    const response = await authConnector.axios.post<AuthServiceResponse<User>>(
      `/user/${action}`,
      action === "register"
        ? {
            ...identity,
            name: firebaseUser.displayName,
            profilePicture: firebaseUser.photoURL,
            role,
          }
        : identity,
      { headers: await authLocationHintService.headers() },
    );
    if (response.data.error || !response.data.success) {
      throw new Error(response.data.message || "Authentication failed. Please try again.");
    }
    const user = response.data.success;
    sessionStorage.setItem("user", JSON.stringify(user));
    sessionStorage.setItem("token", idToken);
    sessionStorage.setItem("firebaseUser", JSON.stringify(firebaseUser));
    store.dispatch(setUser(user));
    return user;
  } catch (error: unknown) {
    if (
      error && typeof error === "object" && "code" in error &&
      (error.code === "auth/popup-closed-by-user" || error.code === "auth/cancelled-popup-request")
    ) {
      return null;
    }
    notifications.show({
      title: `Error with ${label} ${action === "register" ? "signup" : "login"}`,
      message: authErrorMessage(error),
      color: "red",
      position: "bottom-left",
      autoClose: false,
    });
    return null;
  }
}

async function startLinkedIn(action: "LOGIN" | "REGISTER", role?: Roles): Promise<null> {
  try {
    await linkedInAuth.start(action, role);
  } catch (error: unknown) {
    notifications.show({ title: "LinkedIn sign-in failed", message: authErrorMessage(error), color: "red" });
  }
  return null;
}

export const AuthService = {
  doSignupWithGoogle: (role: Roles): Promise<User | null> =>
    authenticate(() => signInWithPopup(getAuth(), new GoogleAuthProvider()), "register", "Google", role),
  doLoginWithGoogle: (): Promise<User | null> =>
    authenticate(() => signInWithPopup(getAuth(), new GoogleAuthProvider()), "login", "Google"),
  doSignupWithLinkedIn: (role: Roles): Promise<User | null> =>
    startLinkedIn("REGISTER", role),
  doLoginWithLinkedIn: (): Promise<User | null> =>
    startLinkedIn("LOGIN"),
  completeLinkedInSignIn: (search: string): Promise<User | null> =>
    authenticate(async () => {
      const customToken = await linkedInAuth.complete(search);
      return signInWithCustomToken(getAuth(), customToken);
    }, "login", "LinkedIn"),
  doSignupWithEmailAndPassword: (
    email: string, password: string, role: Roles,
  ): Promise<User | null> =>
    authenticate(
      () => createUserWithEmailAndPassword(getAuth(), email, password),
      "register", "email", role,
    ),
  doLoginWithEmailAndPassword: (email: string, password: string): Promise<User | null> =>
    authenticate(
      () => signInWithEmailAndPassword(getAuth(), email, password),
      "login", "email",
    ),

  doSendPasswordResetEmail: async (email: string): Promise<boolean> => {
    try {
      await sendPasswordResetEmail(getAuth(), email);
      notifications.show({
        title: "Password Reset Email Sent",
        message: "Check your inbox for a link to reset your password.",
        color: "green",
      });
      return true;
    } catch (error: unknown) {
      notifications.show({
        title: "Error Sending Reset Email",
        message: authErrorMessage(error),
        color: "red",
      });
      return false;
    }
  },

  logout: async (): Promise<void> => {
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("firebaseUser");
    store.dispatch(setUser(null));
  },
};
